import React, { useEffect, useRef, useState } from 'react'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

export type CellMove = 'down' | 'right' | 'left' | 'stay' | 'insert'

export interface CellEditorProps {
  initial: string
  validate: (v: string) => string | null
  onCommit: (v: string, move: CellMove) => void
  onCancel: () => void
  align?: 'left' | 'right'
  type?: 'text' | 'number'
}

/**
 * Shared shell for every inline grid editor (spec B §9.3): an auto-focused `Input` with the text
 * pre-selected, synchronous local validation (`border-destructive` + a `title` tooltip on error,
 * nothing sent), and the Enter/Tab/Shift+Tab/Escape/blur key map. Key events are stopped from
 * bubbling so the grid's own keyboard handler (`useGanttKeyboard`) never sees them.
 */
const CellEditor: React.FC<CellEditorProps> = ({ initial, validate, onCommit, onCancel, align = 'left', type = 'text' }) => {
  const [value, setValue] = useState(initial)
  const [error, setError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  // Guards against a double commit/cancel: committing or cancelling unmounts this editor, which
  // fires a native blur — without this, that blur would re-invoke onCommit('stay') right after.
  const doneRef = useRef(false)

  useEffect(() => {
    const el = inputRef.current
    if (el) { el.focus(); el.select() }
  }, [])

  const commit = (move: CellMove) => {
    if (doneRef.current) return
    const err = validate(value)
    if (err) { setError(err); return }
    doneRef.current = true
    onCommit(value, move)
  }
  const cancel = () => {
    if (doneRef.current) return
    doneRef.current = true
    onCancel()
  }

  return (
    <Input
      ref={inputRef}
      type={type}
      value={value}
      onChange={(e) => { setValue(e.target.value); if (error) setError(null) }}
      className={cn('h-7 px-1 text-sm', align === 'right' && 'text-right', error && 'border-destructive')}
      title={error || undefined}
      onKeyDown={(e) => {
        e.stopPropagation()
        switch (e.key) {
          case 'Enter': e.preventDefault(); commit(e.shiftKey ? 'insert' : 'down'); return
          case 'Insert': e.preventDefault(); commit('insert'); return
          case 'Tab': e.preventDefault(); commit(e.shiftKey ? 'left' : 'right'); return
          case 'Escape': e.preventDefault(); cancel(); return
          default:
        }
      }}
      onKeyUp={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
      onBlur={() => commit('stay')}
    />
  )
}

export default CellEditor
